import React from 'react';
import {
  Delivery,
  DeliveryItem
} from '../../../../../core-module/models/OrderHistory/OrderHistory';
import { Alert, Button, notification, Spin } from 'antd';
import { delivererClient } from '../../../../../core-module/services/deliverer/DelivererClient';
import { useWeeklyMenu } from '../../../../../core-module/hooks/useWeeklyMenu';
import { OrderEditItemFormComponent } from './OrderEditItemFormComponent';
import { OrderEditAddProductsFormComponent } from './OrderEditAddProductsFormComponent';
import { useStockAvailability } from '../../../../../core-module/hooks/useStockAvailability';
import { ItemWithName } from '../../../../../core-module/services/deliverer/types';

interface Props {
  readonly delivery: Delivery;
  readonly onHideClick: () => void;
  readonly handleSalesOrderUpdate?: () => Promise<void>;
}

const MIN_ITEMS_IN_ORDER = 2;

const OrderEditFormContent: React.FC<Props> = ({
  delivery,
  onHideClick,
  handleSalesOrderUpdate
}) => {
  const nonRejectedItemsInDelivery = delivery.items.filter(
    (item) => item.rejectionStatus === 'None'
  );

  const { loading: isLoadingWeeklyMenu, weeklyMenu, error: weeklyMenuError } = useWeeklyMenu(
    delivery.requestedDeliveryMoment?.toString() || undefined
  );
  const allProducts =
    weeklyMenu?.boxes && weeklyMenu?.items
      ? ([...weeklyMenu.boxes, ...weeklyMenu.items] as ItemWithName[])
      : [];
  const { stock, isLoading: isLoadingStock } = useStockAvailability(
    delivery.requestedDeliveryMoment.toString(),
    allProducts?.flatMap((p) => p.portions.map((portion) => portion.productId)) ?? []
  );

  async function handleUpdateQuantity(item: DeliveryItem, newQuantity: number) {
    if (item.quantity === newQuantity) {
      return;
    }

    if (newQuantity <= 0) {
      notification.error({
        message: 'Ongeldig aantal',
        description:
          'Het aantal moet minimaal één zijn. Voor annuleren van een item, gebruik de annuleer knop.'
      });
      return;
    }

    const newTotalQuantity = nonRejectedItemsInDelivery
      .map((i) =>
        i.salesOrderItemId === item.salesOrderItemId ? { ...i, quantity: newQuantity } : i
      )
      .reduce((total, item) => total + item.quantity, 0);

    if (newTotalQuantity < MIN_ITEMS_IN_ORDER) {
      notification.error({
        message: 'Fout bij aanpassen aantal',
        description: `Er moeten minimaal ${MIN_ITEMS_IN_ORDER} producten in een bestelling zitten. Aanpassen van dit item is niet mogelijk.`
      });
      return;
    }

    try {
      await delivererClient.changeSalesOrderItemQuantity(
        delivery.salesOrderId,
        item.salesOrderItemId,
        {
          value: newQuantity
        }
      );

      if (handleSalesOrderUpdate) {
        await handleSalesOrderUpdate();
      }

      notification.success({
        message: 'Opslaan gelukt.',
        description: 'Het aantal aangepast. Het kan even duren voordat dit overal zichtbaar is.'
      });
    } catch (error) {
      notification.error({
        message: 'Fout bij opslaan',
        description: 'Het aantal kon niet aangepast worden. Probeer het opnieuw.'
      });
    }
  }

  async function handleCancelItem(itemToCancel: DeliveryItem) {
    const newItems = nonRejectedItemsInDelivery.filter(
      (i) =>
        i.salesOrderItemId !== itemToCancel.salesOrderItemId &&
        i.parentSalesOrderItemId !== itemToCancel.salesOrderItemId
    );
    const newTotalQuantity = newItems.reduce((total, item) => total + item.quantity, 0);

    if (newTotalQuantity < MIN_ITEMS_IN_ORDER) {
      notification.error({
        message: 'Fout bij annuleren',
        description: `Er moeten minimaal ${MIN_ITEMS_IN_ORDER} producten in één bestelling zitten. Annuleren van dit item is niet mogelijk. Ga terug als je de hele bestelling wilt annuleren.`
      });
      return;
    }

    try {
      await delivererClient.cancelSalesOrderItem(
        delivery.salesOrderId,
        itemToCancel.salesOrderItemId
      );

      if (handleSalesOrderUpdate) {
        await handleSalesOrderUpdate();
      }

      notification.success({
        message: 'Annulering is gelukt',
        description:
          'Het item is succesvol geannuleerd. Het kan even duren voordat dit overal zichtbaar is.'
      });
    } catch (error) {
      notification.error({
        message: 'Fout bij annuleren',
        description: 'Het item kon niet geannuleerd worden. Probeer het opnieuw.'
      });
    }
  }

  async function handleOrderProducts(productIds: Record<string, number>) {
    if (Object.keys(productIds).length === 0) {
      return;
    }

    const request = {
      Items: Object.entries(productIds).map(([key, value]) => ({
        productId: key,
        quantity: value
      }))
    };

    try {
      await delivererClient.addItemsToSalesOrderDelivery(
        delivery.salesOrderId,
        delivery.deliveryId,
        request
      );

      if (handleSalesOrderUpdate) {
        await handleSalesOrderUpdate();
      }

      notification.success({
        message: 'Opslaan gelukt.',
        description: 'De producten zijn toegevoegd. Het kan even duren voordat ze zichtbaar zijn.'
      });
    } catch (error) {
      notification.error({
        message: 'Fout bij opslaan',
        description: 'De producten konden niet worden toegevoegd. Probeer het opnieuw.'
      });
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1em' }}>
        <Button
          type='default'
          style={{ marginLeft: 3, marginRight: 3 }}
          onClick={() => onHideClick()}
        >
          Terug
        </Button>
      </div>

      <h2 style={{ borderBottom: '1px solid #e8e8e8', padding: '0.5em' }}>
        Bestelling {delivery.salesOrderId}
      </h2>

      {(isLoadingStock || isLoadingWeeklyMenu) && (
        <div style={{ textAlign: 'center', padding: '0.5em' }}>
          <Spin size='large' tip='Bestelling wordt opgehaald...' />
        </div>
      )}

      {!(isLoadingStock || isLoadingWeeklyMenu) && stock && (
        <>
          {delivery.items.map((item) => (
            <OrderEditItemFormComponent
              key={item.salesOrderItemId}
              item={item}
              onUpdateQuantity={handleUpdateQuantity}
              onCancelItem={handleCancelItem}
            />
          ))}
          {weeklyMenuError ? (
            <Alert
              style={{ display: 'block', marginTop: 15, marginBottom: 15 }}
              type='warning'
              message='Voor dit bezorgmoment is geen menu gevonden. Producten toevoegen is niet mogelijk.'
            />
          ) : (
            <OrderEditAddProductsFormComponent
              products={allProducts}
              stock={stock}
              onOrderProducts={handleOrderProducts}
            />
          )}
        </>
      )}
    </>
  );
};

export { OrderEditFormContent };
